import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import NavBar from "@/components/NavBar";
import { ArrowLeft, Check, Layers, FileText } from "lucide-react";
import ProductThumbnail from "@/components/catalog/ProductThumbnail";

export default function AdminCatalogViewPage() {
  const { catalogId } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [showBuilder, setShowBuilder] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/catalogs/${catalogId}`);
      setCatalog(data.catalog);
      setProducts(data.products || []);
    } catch (_e) {
      navigate("/admin/catalogs");
    } finally { setLoading(false); }
  }, [catalogId, navigate]);
  useEffect(() => { load(); }, [load]);

  const toggle = (pid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const selectedRefs = useMemo(
    () => products
      .filter((p) => selected.has(p.product_id))
      .map((p) => ({ vendor_id: p.vendor_id, catalog_id: p.catalog_id, product_id: p.product_id })),
    [selected, products],
  );

  if (loading) return <><NavBar /><div className="p-10">Loading…</div></>;
  if (!catalog) return null;

  return (
    <>
      <NavBar />
      <main className="max-w-7xl mx-auto px-5 sm:px-10 py-10">
        <button
          type="button"
          onClick={() => navigate("/admin/catalogs")}
          data-testid="admin-catalog-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#7A7A7A] hover:text-[#1A422D]"
        >
          <ArrowLeft size={14} /> Back to catalogs
        </button>

        <div className="mt-4 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Vendor Catalog · B2B View</div>
            <h1 className="font-serif text-4xl text-[#1A422D] mt-2">{catalog.name}</h1>
            <div className="mt-1 text-xs text-[#7A7A7A]">
              {catalog.season || "—"} · {catalog.year || "—"} · {catalog.catalog_type || "General"}
            </div>
            {catalog.description && (
              <p className="mt-3 text-sm text-[#4A4A4A] max-w-2xl">{catalog.description}</p>
            )}
            {catalog.pdf_file_id && (
              <a
                href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/vendor/catalogs/${catalog.catalog_id}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-catalog-pdf-link"
                className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#B59A5A] hover:text-[#1A422D]"
              >
                <FileText size={13} /> Open Vendor Catalog PDF
              </a>
            )}
          </div>
          {selected.size > 0 && (
            <div className="p-4 border border-[#1A422D] bg-[#F9F8F4]" data-testid="admin-builder-cta">
              <div className="text-xs text-[#1A422D] font-medium">{selected.size} product{selected.size === 1 ? "" : "s"} selected</div>
              <button
                onClick={() => setShowBuilder(true)}
                data-testid="admin-builder-open"
                className="mt-2 btn-primary text-xs inline-flex items-center gap-2"
              >
                <Layers size={13} /> Create Member Collection
              </button>
            </div>
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-b2b-products">
          {products.map((p) => {
            const isSel = selected.has(p.product_id);
            return (
              <button
                key={p.product_id}
                type="button"
                onClick={() => toggle(p.product_id)}
                data-testid={`admin-b2b-product-${p.product_id}`}
                className={`text-left border transition-colors ${isSel ? "border-[#1A422D] bg-[#F9F8F4]" : "border-[#E5E3D8] bg-white hover:border-[#B59A5A]"}`}
              >
                <ProductThumbnail product={p} size="card" testId={`admin-b2b-thumb-${p.product_id}`} />
                <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">
                    {p.availability_status || "available"}
                  </div>
                  <div className={`w-5 h-5 border flex items-center justify-center ${isSel ? "bg-[#1A422D] border-[#1A422D] text-white" : "border-[#B59A5A]"}`}>
                    {isSel && <Check size={12} />}
                  </div>
                </div>
                <div className="mt-2 font-serif text-lg text-[#1A422D]">{p.product_name}</div>
                <div className="mt-1 text-xs text-[#7A7A7A]">
                  {p.style_number ? `Style #${p.style_number} · ` : ""}
                  {p.category || "—"}
                </div>
                {p.description && (
                  <div className="text-xs text-[#4A4A4A] mt-2 line-clamp-2">{p.description}</div>
                )}
                <div className="text-xs text-[#4A4A4A] mt-3">
                  {p.wholesale_price != null && <>Wholesale <span className="font-medium">${p.wholesale_price}</span></>}
                  {p.msrp != null && <> · MSRP <span className="font-medium">${p.msrp}</span></>}
                </div>
                {(p.colors?.length || 0) > 0 && (
                  <div className="text-[10px] text-[#7A7A7A] mt-1">Colors: {p.colors.join(", ")}</div>
                )}
                </div>
              </button>
            );
          })}
        </div>
      </main>

      {showBuilder && (
        <CollectionBuilderModal
          refs={selectedRefs}
          onClose={() => setShowBuilder(false)}
          onCreated={(col) => {
            setShowBuilder(false); setSelected(new Set());
            navigate("/admin/catalogs");
          }}
        />
      )}
    </>
  );
}

function CollectionBuilderModal({ refs, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [audience, setAudience] = useState("all_members");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [publishNow, setPublishNow] = useState(true);

  const submit = async () => {
    if (!name.trim()) { setError("Collection name is required."); return; }
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/admin/member-collections", {
        name: name.trim(),
        description: description.trim() || undefined,
        product_refs: refs,
        audience_type: audience,
        allow_product_requests: true,
        allow_wishlist: true,
        allow_interested: true,
      });
      if (publishNow) {
        await api.post(`/admin/member-collections/${data.collection_id}/publish`);
      }
      onCreated?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Could not create collection.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="collection-builder-modal">
      <div className="bg-white w-full max-w-lg p-8 border border-[#E5E3D8]">
        <div className="overline">Build Collection</div>
        <h2 className="font-serif text-2xl text-[#1A422D] mt-1">Create Member Collection</h2>
        <p className="text-xs text-[#7A7A7A] mt-1">{refs.length} products selected</p>

        <div className="mt-6 space-y-3">
          <div>
            <label className="label-elegant block mb-1">Collection Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="builder-name" className="input-field" placeholder="Spring Arrivals" />
          </div>
          <div>
            <label className="label-elegant block mb-1">Description</label>
            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="builder-desc" className="input-field" />
          </div>
          <div>
            <label className="label-elegant block mb-1">Audience</label>
            <select value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="builder-audience" className="input-field">
              <option value="all_members">All Members</option>
              <option value="selected_members">Selected Members (edit later)</option>
              <option value="member_group">Member Group (edit later)</option>
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-[#1A1A1A]">
            <input type="checkbox" checked={publishNow} onChange={(e) => setPublishNow(e.target.checked)} data-testid="builder-publish-now" />
            Publish immediately after saving
          </label>
        </div>

        {error && <div className="mt-4 text-xs text-[#a33]" data-testid="builder-error">{error}</div>}

        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} data-testid="builder-cancel" className="btn-secondary text-xs">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="builder-submit" className="btn-primary">
            {busy ? "Creating…" : "Create Collection"}
          </button>
        </div>
      </div>
    </div>
  );
}
